import { HttpClient } from '../../http/http-client/http-client';
import { HttpPollingRequestInit } from '../../http/http-request/http-request';
import { Logger } from '../../../logger/logger';
import { wrap } from 'comlink';
import { HttpUtilService } from '../../http/http-util-service';
import { UtilWorker } from '../../worker/util-worker';
import { AgentSessionManager } from '../session/agent-session-manager';
import { RetryOptions } from '../../model/retry-options/retry-options';
import { StorageKeys } from '../../../constants/storage-key';

import { ApiUriConstants } from '../../../constants/api-uri-constants';
import { CXoneGetNextAdapter } from '../../adapter/cxone-get-next-adapter';
import { GetNextEventResponse } from '../model/get-next-event-response';

/**
 * GetNextEventProvider to perform getNextEvent polling
 */
export class GetNextEventProvider {
  private static singleton: GetNextEventProvider;
  private getNextPollingWorker: any;
  private baseUri = '';
  logger: Logger = new Logger('lib.Agent', 'lib.GetNextEventProvider');
  protected utilService: HttpUtilService = new HttpUtilService();
  private getNextEventAdapter: CXoneGetNextAdapter = new CXoneGetNextAdapter();
  private agentSession: AgentSessionManager;

  /**
   * constructor which sets agent session instance
   */
  private constructor() {
    this.agentSession = AgentSessionManager.instance;
  }

  /**
   * The static method that controls the access to the singleton instance.
   * @example
   * ```
   * const getNextEventProvider = GetNextEventProvider.instance();
   * ```
   */
  public static get instance(): GetNextEventProvider {
    if (!GetNextEventProvider.singleton) {
      GetNextEventProvider.singleton = new GetNextEventProvider();
    }
    return GetNextEventProvider.singleton;
  }

  /**
   * Starts GetNextEvents for specified agent session
   *   @example
   * ```
   * this.startGetNextEvents();
   * ```
   */
  public startGetNextEvents() {
    this.logger.info(
      'startGetNextEvents',
      'startGetNextEvents in getNextEventProvider'
    );
    this.baseUri = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    if (!this.getNextPollingWorker) this.getNextEvents(this.onPollingExecuted);
  }

  /**
   * Calls get-next-event api on worker thread
   *   @example
   * ```
   * this.getNextEvents(this.onPollingExecuted, retryOptions);
   * ```
   */
  private getNextEvents(
    callback: (data: GetNextEventResponse) => void,
    retryOptions?: RetryOptions
  ) {
    const { startPolling } = this.initUtilWorker();
    const sessionId = this.agentSession.getSessionId();
    if (!sessionId) {
      return;
    }
    const getNextPath = ApiUriConstants.GET_NEXT_EVENT_URI.replace(
      '{sessionId}',
      sessionId
    ).replace('{timeoutSec}', ApiUriConstants.GET_NEXT_EVENT_TIMEOUT);

    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const endpoint = this.baseUri + getNextPath;
    const reqInit: HttpPollingRequestInit = {
      headers: this.utilService.initPollingHeader(
        authToken,
        'application/json'
      ),
      method: 'GET',
    };

    HttpClient.getWithPolling(
      {url: endpoint,request:reqInit},
      callback,
      startPolling,
      {retryOptions:retryOptions}
    );
  }

  /**
   * Captures data returned from get-next-event api
   * @param data - collection of events
   */
  private onPollingExecuted = (data: GetNextEventResponse) => {
    if (data?.error) {
      this.logger.info(
        'onPollingExecuted',
        'Failure callback in getNextEventProvider'
      );
      const retryOptions: RetryOptions = {
        maxRetryAttempts: 3,
        retryInterval: 10000,
      };
      this.getNextEvents(this.onPollingRetrySuccess, retryOptions);
    } else {
      this.logger.info(
        'onPollingExecuted',
        'Success callback in getNextEventProvider'
      );
      // update with new sessionId
      if (data?.sessionId) {
        localStorage.setItem(StorageKeys.ACD_SESSION_ID, data.sessionId);
        this.agentSession.setSessionId(data?.sessionId);
      }
      this.getNextEvents(this.onPollingExecuted);
      if (data?.events && data?.events.length > 0)
        this.getNextEventAdapter.handleGetNextResponse(data.events);
    }
  };

  /**
   * Captures data returned from get-next-event api after successful retry
   * @param data - collection of events
   */
  private onPollingRetrySuccess = (data: GetNextEventResponse) => {
    // Success on retry
    if (!data?.error) {
      this.logger.info(
        'onPollingExecuted',
        'Success callback in getNextEventProvider'
      );
      this.terminateUtilWorker();
      // update with new sessionId
      if (data?.sessionId) {
        localStorage.setItem(StorageKeys.ACD_SESSION_ID, data.sessionId);
        this.agentSession.setSessionId(data?.sessionId);
      }
      this.getNextEvents(this.onPollingExecuted);
      if (data?.events && data?.events.length > 0)
        this.getNextEventAdapter.handleGetNextResponse(data.events);
    }
  };

  /**
   * Use to initializing the util worker and will return the method inside the worker
   * @example
   * ```
   * this.initUtilWorker();
   * ```
   */
  public initUtilWorker() {
    if (!this.getNextPollingWorker)
      this.getNextPollingWorker = new Worker('../../worker/util-worker.ts', {
        name: 'ccf-getnextevent-polling-worker',
        type: 'module',
      });
    return wrap<UtilWorker>(this.getNextPollingWorker);
  }

  /**
   * Use to terminate the util worker instance
   * @example
   * ```
   * this.terminateUtilWorker();
   * ```
   */
  public terminateUtilWorker() {
    this.getNextPollingWorker.terminate();
    this.getNextPollingWorker = undefined;
  }
}
